import {
    ButtonFlavor,
    ButtonSize,
    ButtonType,
    ExButton,
    ExIcon,
    ExIconButton,
    IconButtonFlavor,
    IconButtonSize,
    IconButtonType,
    IconSize,
} from '@boomi/exosphere';
import debounce from 'lodash.debounce';
import { type Reducer, useCallback, useEffect, useReducer, useState } from 'react';
import {
    type LoaderFunctionArgs,
    type To,
    useNavigate,
    useParams,
    useSearchParams,
} from 'react-router-dom';
import Loading from '../Loading';
import { type AutomateError, getAuthenticationStatus } from '../api';
import { useApp } from '../common/AppProvider';
import ConfirmationDialog from '../common/ConfirmationDialog';
import { signOut } from '../okta';
import { translations } from '../translations/translations';
import {
    type AuthenticationStatusResponse,
    type AutomationTaskEdit,
    AutomationTaskStatus,
    type Output,
    type Action as TypeAction,
    type Trigger as TypeTrigger,
} from '../types/types';
import {
    defaultValues,
    isConfigValid,
    isTaskAuthenticated,
    pickObjProps,
    stringReplace,
} from '../utils/utils';
import Drawer from './Drawer';
import TaskEditorFooter from './TaskEditorFooter';
import TaskEditorHeader from './TaskEditorHeader';
import TriggerOrActionDrawerManager from './TriggerOrActionDrawerManager';
import TriggerOrActionSummary from './TriggerOrActionSummary';
import type { TaskEditAction } from './taskReducer';
import { taskReducer } from './taskReducer';

import './task-editor.css';

type SelectedItemType = 'trigger' | 'action';
type SelectedItemData = TypeTrigger | TypeAction | null;
type SelectedItemIndex = number | null;

export type SelectedTaskItem = {
    itemType: SelectedItemType;
    itemData: SelectedItemData;
    itemIndex: SelectedItemIndex;
};

export const loader = ({ params }: LoaderFunctionArgs) => params;

interface Params {
    debounceTestOverride?: number; // used for testing only
}

const TaskEditor = ({ debounceTestOverride }: Params) => {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const {
        tasks,
        syncAndUpdate,
        connectors,
        language,
        isLoading,
        alertSuccess,
        alertError,
        testTask,
        isOnline,
        unsavedChanges,
        setUnsavedChanges,
        unsavedChangesNavigateTo,
        setUnsavedChangesNavigateTo,
    } = useApp();
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isTesting, setIsTesting] = useState(false);
    const [authenticationStatus, setAuthenticationStatus] = useState<AuthenticationStatusResponse>(
        {},
    );
    const [isLoadingAuthenticationStatus, setIsLoadingAuthenticationStatus] = useState(true);
    const [task, dispatchTask] = useReducer<Reducer<AutomationTaskEdit | null, TaskEditAction>>(
        taskReducer,
        null,
    );
    const [openDrawer, setOpenDrawer] = useState<boolean>(false);
    const [selectedTaskItem, setSelectedTaskItem] = useState<SelectedTaskItem | null>(null);
    const [showItemPlaceholder, setShowItemPlaceholder] = useState<boolean>(false);
    const [showUnsavedHint, setShowUnsavedHint] = useState<boolean>(true);

    const showTriggerPlaceholder = showItemPlaceholder && selectedTaskItem?.itemType === 'trigger';
    const showActionPlaceholder = showItemPlaceholder && selectedTaskItem?.itemType === 'action';

    // Selecting a trigger or action in the task that should be configured...
    // ...or clicking "Add Trigger"/"Add Action" buttons and configuring a new one.
    const selectTaskItem = (
        itemType: SelectedItemType,
        itemData: SelectedItemData,
        itemIndex: SelectedItemIndex,
    ) => {
        setShowItemPlaceholder(!itemData);
        setSelectedTaskItem({
            itemType,
            itemData,
            itemIndex,
        });
        setOpenDrawer(true);
    };

    const handleCloseDrawer = () => {
        setOpenDrawer(false);
        setShowItemPlaceholder(false);
        // Delay the removal of the drawer content, so it's removed after the drawer
        // is done closing (animation is done).
        setTimeout(() => setSelectedTaskItem(null), 250);
    };

    // Get authentication statuses for all connectors. Doing this here can save us some status
    // requests down the line.
    const loadConnectorsAuthenticationStatus = useCallback(async () => {
        try {
            setIsLoadingAuthenticationStatus(true);
            const status = await getAuthenticationStatus();
            setAuthenticationStatus(status);
        } catch (error) {
            alertError({
                message: translations[language].ERROR_getting_authentication_status,
                traceId: (error as AutomateError).json?.traceId,
            });
        } finally {
            setIsLoadingAuthenticationStatus(false);
        }
    }, [language, alertError]);

    // When we close the authentication popup we need to re-get the connectors/apps authentication
    // status because it is now likely changed. Scenario: we added a new action and authenticated
    // its connector.
    const onAuthenticated = useCallback(() => {
        loadConnectorsAuthenticationStatus();
    }, [loadConnectorsAuthenticationStatus]);

    // Getting a task created/loaded into the reducer.
    useEffect(() => {
        loadConnectorsAuthenticationStatus();

        // If we came here by clicking an existing task (/tasks/{someTaskId}) then find and load
        // that task into the reducer and get any related connector authentication statuses.
        if (id) {
            const task = tasks.find((t) => t.id === id);

            if (task) {
                task.status = AutomationTaskStatus.Modified;
                dispatchTask({
                    type: 'setTask',
                    payload: { task },
                });
                return;
            }
        }

        // If we came here via New Task and selected a trigger and an action
        // (/tasks/new-task/?trigger-connector=...&trigger-event=...&action-connector=...&action-event=...)
        // then create a new task based on those selected query params.
        if (searchParams.size && Object.keys(connectors).length) {
            const triggerConnector = searchParams.get('trigger-connector');
            const triggerEvent = searchParams.get('trigger-event');
            const actionConnectors = searchParams.getAll('action-connector');
            const actionEvents = searchParams.getAll('action-event');
            const trigger =
                triggerConnector && triggerEvent
                    ? connectors[triggerConnector]?.triggers[triggerEvent]
                    : null;
            const actions =
                actionConnectors.length &&
                actionEvents.length &&
                actionConnectors.length === actionEvents.length
                    ? actionConnectors.map((c, i) => connectors[c]?.actions[actionEvents[i]])
                    : null;

            if (trigger && actions?.length) {
                const actionsName =
                    actions?.length > 1 ? actions.map((a) => a.name).join(' + ') : actions[0].name;

                const newTask = {
                    displayName: `${trigger.name} -> ${actionsName}`,
                    trigger,
                    actions: [...actions],
                    status: AutomationTaskStatus.Modified,
                    isActive: true,
                    test: false,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                };

                dispatchTask({
                    type: 'setTask',
                    payload: {
                        task: newTask,
                    },
                });
                // a new task (created with the wizard) starts out as unsaved
                setUnsavedChanges(true);
                setShowUnsavedHint(true);
                return;
            }
        }

        // Otherwise if we didn't select anything (/tasks/new-task) create a blank new task.
        if (!id) {
            dispatchTask({
                type: 'setTask',
                payload: {
                    task: {
                        displayName: '',
                        trigger: null,
                        actions: [],
                        status: AutomationTaskStatus.Modified,
                        isActive: true,
                        test: false,
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                    },
                },
            });
        }
    }, [
        id,
        tasks,
        connectors,
        searchParams,
        loadConnectorsAuthenticationStatus,
        setUnsavedChanges,
    ]);

    // Update unsaved changes status.
    useEffect(() => {
        // Nothing to do if task is not present.
        if (!task) {
            return;
        }

        const savedTask = tasks.find((t) => t.id === id);

        // A blank new task (made using the Shift+New shortcut) is not considered
        // to have unsaved changes by default when just created because
        // it starts off as completely blank without any trigger and actions, so by
        // leaving the editor we'd lose nothing.
        if (!savedTask && !task.displayName && !task.trigger && !task.actions.length) {
            return;
        }

        // Picking out only relevant props that the users can directly change by editing a task,
        // any other props that are likely to change like updatedAt, lastActivated, activations, etc.
        // are ignored because they would just cause false positives.
        const relevantProps = ['displayName', 'isActive', 'trigger', 'actions'];
        const savedTaskRelevantProps = savedTask && pickObjProps(savedTask, relevantProps);
        const taskRelevantProps = task && pickObjProps(task, relevantProps);

        if (JSON.stringify(savedTaskRelevantProps) !== JSON.stringify(taskRelevantProps)) {
            setUnsavedChanges(true);
            setShowUnsavedHint(true);
            return;
        }

        // No changes were found, we can clear any leftover unsaved changes indicators.
        setUnsavedChanges(false);
        setShowUnsavedHint(false);
    }, [id, task, tasks, setUnsavedChanges]);

    if (!task) {
        return null;
    }

    const checkTaskForErrors = () => {
        const errors: string[] = [];

        if (!task.displayName) {
            errors.push(translations[language].TASK_validation_name);
        }

        if (!isTaskAuthenticated(task, authenticationStatus)) {
            errors.push(translations[language].TASK_validation_authentication);
        }

        if (!task.trigger) {
            errors.push(translations[language].TASK_validation_trigger);
        }

        if (task.trigger && !isConfigValid(task.trigger, language)) {
            errors.push(
                `${task.trigger.providerId.toUpperCase()}: ${
                    translations[language].TASK_validation_incomplete_trigger_config
                }`,
            );
        }

        if (task.actions.length === 0) {
            errors.push(translations[language].TASK_validation_actions);
        }

        if (task.actions.length !== 0) {
            for (const action of task.actions) {
                if (!isConfigValid(action, language)) {
                    errors.push(
                        `${action.providerId.toUpperCase()}: ${
                            translations[language].TASK_validation_incomplete_action_config
                        }`,
                    );
                }
            }
        }

        setValidationErrors(errors);

        return errors;
    };

    const navigateAway = (navTo: To | null) => {
        if (!navTo) {
            return;
        }

        if (navTo === 'signOut') {
            signOut();
            return;
        }

        navigate(navTo);
    };

    const onUpdateName = (taskName: string) => {
        dispatchTask({
            type: 'updateName',
            payload: { displayName: taskName },
        });
    };

    const onToggle = (task: AutomationTaskEdit) => {
        if (!task) {
            alertError({
                message: translations[language].ERROR_unknown,
            });
            return;
        }

        dispatchTask({
            type: 'updateActiveState',
            payload: { isActive: !task.isActive },
        });
    };

    // Debounce to prevent duplicate tasks being created.
    const onSave = debounce(async () => {
        if (checkTaskForErrors().length) {
            return;
        }

        setIsSubmitted(true);

        const response = await syncAndUpdate([{ ...task, updatedAt: new Date().toISOString() }]);

        if (!response) {
            alertError({
                message: translations[language].ERROR_unknown,
            });
            return;
        }

        const isNewTask = !id;
        if (response.taskErrors.length > 0) {
            alertError({
                message: isNewTask
                    ? translations[language].TASK_create_with_errors
                    : translations[language].TASK_save_with_errors,
            });
        } else {
            alertSuccess({
                message: isNewTask
                    ? translations[language].TASK_create_success_message
                    : translations[language].TASK_save_success_message,
            });
        }

        setUnsavedChanges(false);
        setShowUnsavedHint(false);
        // If we tried to navigate away with unsaved changes use that links location as the default
        // location to navigate to, otherwise check if we just created and saved a brand-new task
        // and navigate to the task list.
        navigateAway(unsavedChangesNavigateTo ?? (isNewTask ? '/tasks' : null));
    }, debounceTestOverride ?? 300);

    const onTest = debounce(async () => {
        if (checkTaskForErrors().length) {
            return;
        }

        setIsSubmitted(true);
        setIsTesting(true);

        try {
            await testTask(task, {});
            alertSuccess({
                message: translations[language].TASK_test_success_message,
            });
        } catch (e) {
            alertError({
                message: (e as Error).message,
            });
        } finally {
            setIsTesting(false);
        }
    }, debounceTestOverride ?? 300);

    const getOutputs = (taskItem: SelectedTaskItem) => {
        if (taskItem.itemType === 'trigger') {
            return [];
        }

        const outputs = new Map<string, Output>();

        for (const output of (task.trigger?.outputs || []).concat(
            task.actions.slice(0, taskItem.itemIndex || 0).flatMap((action) => action.outputs),
        )) {
            outputs.set(output.name, output);
        }

        return Array.from(outputs.values());
    };

    return (
        <>
            <main className="automate-content task-editor-layout">
                <TaskEditorHeader
                    taskId={id}
                    taskName={task.displayName}
                    isInvalidTaskName={isSubmitted && !task.displayName}
                    isTaskActive={task.isActive}
                    isEditingTask={!!selectedTaskItem}
                    isToggleDisabled={!isTaskAuthenticated(task, authenticationStatus) && isOnline}
                    onUpdateName={onUpdateName}
                    onToggle={() => onToggle(task)}
                    onTest={onTest}
                    onSave={onSave}
                />

                <section className="task-editor-content">
                    {validationErrors.length ? (
                        <section className="task-validation-errors">
                            {validationErrors.map((error) => (
                                <p key={error}>{error}</p>
                            ))}
                        </section>
                    ) : null}

                    <section className="task-item-trigger-section">
                        <h3 className="text lg">
                            {translations[language].TASK_trigger_section_title}
                        </h3>
                    </section>

                    <section className="task-item">
                        {task.trigger ? (
                            <TriggerOrActionSummary
                                itemType={'trigger'}
                                itemData={task.trigger}
                                onOpenConfig={() =>
                                    selectTaskItem(
                                        'trigger',
                                        task?.trigger && {
                                            ...task.trigger,
                                            values: defaultValues(
                                                task.trigger.options,
                                                task.trigger.values,
                                            ),
                                        },
                                        null,
                                    )
                                }
                                onDeleteItem={() => {
                                    dispatchTask({
                                        type: 'deleteTrigger',
                                        payload: null,
                                    });
                                    handleCloseDrawer();
                                }}
                                isSelected={
                                    task.trigger.id === selectedTaskItem?.itemData?.id &&
                                    selectedTaskItem?.itemType === 'trigger'
                                }
                                authenticationStatus={authenticationStatus}
                                isLoadingAuthenticationStatus={isLoadingAuthenticationStatus}
                            />
                        ) : showTriggerPlaceholder ? (
                            <div className="task-item-placeholder ta-tile">
                                <ExIcon className="placeholder-icon" icon="Edit" />
                            </div>
                        ) : (
                            <ExButton
                                data-testid="addTriggerButton"
                                className="exo-button"
                                flavor={ButtonFlavor.BASE}
                                size={ButtonSize.LARGE}
                                type={ButtonType.PRIMARY}
                                onClick={() => {
                                    selectTaskItem('trigger', null, null);
                                }}
                            >
                                <ExIcon icon="plus" slot="prefix" hide-browser-tooltip />
                                {stringReplace(
                                    translations[language].TASK_add_task_item,
                                    translations[language].TASK_item_type_trigger,
                                )}
                            </ExButton>
                        )}
                    </section>

                    <section className="task-item-action-section">
                        <h3 className="text lg">
                            {translations[language].TASK_actions_section_title}
                        </h3>
                    </section>

                    {(task.actions.length > 0 || showActionPlaceholder) && (
                        <ul className="task-item-action-list">
                            {task.actions.map((action, index) => (
                                <li
                                    key={`actionSectionAction_${action.name.replace(/ /g, '')}`}
                                    className="task-item"
                                >
                                    <TriggerOrActionSummary
                                        itemType={'action'}
                                        itemData={action}
                                        onOpenConfig={() =>
                                            selectTaskItem(
                                                'action',
                                                action && {
                                                    ...action,
                                                    values: defaultValues(
                                                        action.options,
                                                        action.values,
                                                    ),
                                                },
                                                index,
                                            )
                                        }
                                        onDeleteItem={() => {
                                            dispatchTask({
                                                type: 'deleteAction',
                                                payload: { index },
                                            });
                                            handleCloseDrawer();
                                        }}
                                        isSelected={
                                            action.id === selectedTaskItem?.itemData?.id &&
                                            selectedTaskItem?.itemType === 'action' &&
                                            index === selectedTaskItem.itemIndex
                                        }
                                        authenticationStatus={authenticationStatus}
                                        isLoadingAuthenticationStatus={
                                            isLoadingAuthenticationStatus
                                        }
                                    />
                                </li>
                            ))}
                            {showActionPlaceholder && (
                                <li className="task-item">
                                    <div className="task-item-placeholder ta-tile">
                                        <ExIcon className="placeholder-icon" icon="Edit" />
                                    </div>
                                </li>
                            )}
                        </ul>
                    )}

                    {!showActionPlaceholder && (
                        <section className="task-item">
                            <ExButton
                                data-testid="addActionButtonTop"
                                className="exo-button"
                                flavor={ButtonFlavor.BASE}
                                type={ButtonType.PRIMARY}
                                size={ButtonSize.LARGE}
                                onClick={() => {
                                    selectTaskItem('action', null, task.actions.length);
                                }}
                            >
                                <ExIcon icon="plus" slot="prefix" hide-browser-tooltip />
                                {stringReplace(
                                    translations[language].TASK_add_task_item,
                                    translations[language].TASK_item_type_action,
                                )}
                            </ExButton>
                        </section>
                    )}
                </section>

                {unsavedChanges && showUnsavedHint && (
                    <div className="task-unsaved-popup">
                        <p className="popup-message text xs">
                            <ExIcon icon="Information" size={IconSize.S} hide-browser-tooltip />
                            {translations[language].TASK_unsaved_message}
                        </p>
                        <ExIconButton
                            className="popup-close"
                            icon="Close"
                            circular={true}
                            flavor={IconButtonFlavor.BRANDED}
                            size={IconButtonSize.SMALL}
                            type={IconButtonType.TERTIARY}
                            onClick={() => setShowUnsavedHint(false)}
                            hideBrowserTooltip
                        />
                    </div>
                )}

                <TaskEditorFooter
                    onSave={onSave}
                    onTest={onTest}
                    taskId={id}
                    isEditingTask={!!selectedTaskItem}
                />

                <Drawer
                    isOpen={openDrawer}
                    onClose={handleCloseDrawer}
                    title={
                        selectedTaskItem
                            ? selectedTaskItem.itemType === 'trigger'
                                ? translations[language].TASK_trigger_section_title
                                : translations[language].TASK_actions_section_title
                            : ''
                    }
                >
                    {selectedTaskItem && (
                        <TriggerOrActionDrawerManager
                            key={selectedTaskItem?.itemData?.id}
                            selectedTaskItem={selectedTaskItem}
                            dispatchTask={dispatchTask}
                            authenticationStatus={authenticationStatus}
                            onAuthenticated={onAuthenticated}
                            onClose={handleCloseDrawer}
                            outputs={getOutputs(selectedTaskItem)}
                            isLoadingAuthenticationStatus={isLoadingAuthenticationStatus}
                        />
                    )}
                </Drawer>

                {(isTesting || isLoading) && (
                    <Loading
                        message={
                            isLoading
                                ? translations[language].COMMON_loading
                                : translations[language].COMMON_testing_actions
                        }
                        fullScreen={true}
                    />
                )}
            </main>

            {unsavedChangesNavigateTo && (
                <ConfirmationDialog
                    title={translations[language].TASK_unsaved_dialog_title}
                    message={translations[language].TASK_unsaved_dialog_message}
                    cancelText={translations[language].TASK_unsaved_dialog_discard}
                    confirmText={translations[language].TASK_unsaved_dialog_save}
                    onClose={() => setUnsavedChangesNavigateTo(null)}
                    onCancel={() => {
                        setUnsavedChanges(false);
                        setShowUnsavedHint(false);
                        setUnsavedChangesNavigateTo(null);
                        navigateAway(unsavedChangesNavigateTo);
                    }}
                    onConfirm={() => {
                        setUnsavedChangesNavigateTo(null);
                        onSave();
                    }}
                />
            )}
        </>
    );
};

export default TaskEditor;
